import React, { useState, useCallback, useMemo } from "react";
import { css } from "glamor";
import { FaCcVisa, FaCcMastercard } from "react-icons/fa6";
import { FaCreditCard } from "react-icons/fa";
import { Message, Icon } from "semantic-ui-react";
import Modal from "../library/Modal";
import Button from "../library/Button";
import { colors, spacing } from "../library/theme";

// --- Styles ---

const modalClass = css({
  borderRadius: "14px !important",
  boxShadow: "0px 20px 25px -5px rgba(0, 0, 0, 0.1), 0px 8px 10px -6px rgba(0, 0, 0, 0.1) !important",
  "&.ui.tiny.modal": {
    maxWidth: "480px",
  },
});

const headerWrapper = css({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
});

const headerLeft = css({
  display: "flex",
  alignItems: "center",
  gap: spacing.xs,
});

const headerTitle = css({
  fontFamily: "Montserrat, sans-serif !important",
  fontSize: 16,
  fontWeight: 600,
  lineHeight: "24px",
  margin: 0,
  color: "#0F172A",
});

const closeButton = css({
  cursor: "pointer",
  color: colors.greyDark,
  fontSize: 18,
  ":hover": { color: colors.black },
});

const savedCardItem = css({
  display: "flex",
  alignItems: "center",
  gap: spacing.s,
  padding: `${spacing.s} ${spacing.m}`,
  border: `1px solid ${colors.greyLight}`,
  borderRadius: 8,
  cursor: "pointer",
  transition: "all 0.2s",
  marginBottom: spacing.s,
  ":hover": {
    borderColor: colors.primary,
  },
});

const savedCardItemSelected = css({
  borderColor: colors.primary,
  backgroundColor: colors.greyClear,
});

const savedCardLabel = css({
  flex: 1,
  fontSize: 14,
  fontWeight: "500",
  color: colors.black,
  display: "flex",
  alignItems: "center",
  gap: spacing.xs,
});

const defaultBadge = css({
  fontSize: 11,
  fontWeight: "600",
  color: colors.primary,
});

const addMethodLink = css({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: spacing.xxs,
  padding: `${spacing.s} ${spacing.m}`,
  border: `1px dashed ${colors.greyLight}`,
  borderRadius: 8,
  cursor: "pointer",
  fontSize: 14,
  color: colors.greyDark,
  transition: "all 0.2s",
  ":hover": {
    borderColor: colors.primary,
    color: colors.primary,
  },
});

const radioStyle = { accentColor: colors.primary };
const errorMessageStyle = { marginBottom: spacing.m };
const actionButtonStyle = { margin: 0 };

// --- Helpers ---

function SavedCardBrandIcon({ brand }) {
  const b = brand?.toLowerCase();
  if (b === "visa") return <FaCcVisa size={22} />;
  if (b === "mastercard") return <FaCcMastercard size={22} />;
  return <FaCreditCard size={22} />;
}

function brandLabel(brand) {
  const b = brand?.toLowerCase();
  if (b === "visa") return "Visa";
  if (b === "mastercard") return "Mastercard";
  if (b === "elo") return "Elo";
  if (b === "amex") return "Amex";
  return brand || "Cartão";
}

// --- Component ---

export default function ChangeCardModal({
  open,
  onClose,
  onSubmit,
  onAddNew,
  loading,
  savedCards,
  currentCardLastDigits,
}) {
  const [selectedCardId, setSelectedCardId] = useState(null);
  const [submitError, setSubmitError] = useState(null);

  const initialCardId = useMemo(() => {
    const current = savedCards.find((c) => c.number === currentCardLastDigits);
    return current?.id || null;
  }, [savedCards, currentCardLastDigits]);

  const effectiveSelectedId = selectedCardId !== null ? selectedCardId : initialCardId;

  const handleClose = useCallback(() => {
    setSelectedCardId(null);
    setSubmitError(null);
    onClose();
  }, [onClose]);

  const handleSaveSelection = useCallback(() => {
    if (!effectiveSelectedId) return;
    const card = savedCards.find((c) => c.id === effectiveSelectedId);
    if (!card) return;

    setSubmitError(null);
    onSubmit({ cardId: card.cardId })
      .then(() => handleClose())
      .catch((error) => {
        const msg =
          error?.response?.body?.error ||
          "Ocorreu um erro ao atualizar o cartão. Tente novamente mais tarde.";
        setSubmitError(msg);
      });
  }, [effectiveSelectedId, savedCards, onSubmit, handleClose]);

  if (!open) return null;

  return (
    <Modal open={open} onClose={handleClose} className={modalClass} size="tiny">
      <Modal.Header>
        <div className={headerWrapper}>
          <div className={headerLeft}>
            <h2 className={headerTitle}>Alterar forma de pagamento</h2>
          </div>
          <Icon name="close" className={closeButton} onClick={handleClose} />
        </div>
      </Modal.Header>

      <Modal.Content>
        {submitError && (
          <Message error visible content={submitError} style={errorMessageStyle} />
        )}

        {savedCards.map((card) => {
          const isSelected = card.id === effectiveSelectedId;
          const isCurrent = card.number === currentCardLastDigits;
          return (
            <div
              key={card.id}
              className={css(savedCardItem, isSelected && savedCardItemSelected)}
              onClick={() => setSelectedCardId(card.id)}
            >
              <input type="radio" checked={isSelected} readOnly style={radioStyle} />
              <SavedCardBrandIcon brand={card.brand} />
              <span className={savedCardLabel}>
                {brandLabel(card.brand)} ****{card.number}
              </span>
              {(card.default || (isCurrent && !savedCards.some((c) => c.default))) && (
                <span className={defaultBadge}>Principal</span>
              )}
            </div>
          );
        })}

        <div className={addMethodLink} onClick={onAddNew}>
          + Adicionar novo método
        </div>
      </Modal.Content>

      <Modal.Actions>
        <Button
          onClick={handleSaveSelection}
          loading={loading}
          disabled={loading || !effectiveSelectedId}
          color="black"
          fluid
          style={actionButtonStyle}
        >
          Salvar alteração
        </Button>
      </Modal.Actions>
    </Modal>
  );
}
